import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobsService, auditorsService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { 
  ArrowLeft, MapPin, Calendar, Users, 
  FileText, Plus, Trash2, Star, Loader2,
  AlertCircle, FolderOpen
} from 'lucide-react';
import { format } from 'date-fns';
import ClientReportsSection from '../components/ClientReportsSection';
import SummaryReportSection from '../components/SummaryReportSection';
import ErrorBoundary from '../components/ErrorBoundary';
import { getApiError } from '@/lib/utils';

// Simplified status labels - aligned with Schedule page
const statusColors = {
  scheduled: 'default',
  completed: 'secondary',
  cancelled: 'destructive'
};

const statusLabels = {
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled'
};

export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canManageJobs, isAdmin, isScheduler } = useAuth();
  const { toast } = useToast();

  const [job, setJob] = useState(null);
  const [auditors, setAuditors] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Dialogs - removed component and status dialogs (Components tab hidden, status changes via Schedule page)
  const [participantDialogOpen, setParticipantDialogOpen] = useState(false);
  const [evidenceDialogOpen, setEvidenceDialogOpen] = useState(false);
  const [deleteParticipantId, setDeleteParticipantId] = useState(null);

  // Forms - removed component and status forms
  const [participantForm, setParticipantForm] = useState({ auditor_id: '', is_primary: false });
  const [evidenceForm, setEvidenceForm] = useState({ evidence_type: 'note', description: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadJob();
    loadAuditors();
  }, [id]);

  const loadJob = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      console.log(`[JobDetail] Loading job ${id}...`);
      const data = await jobsService.get(id);
      console.log(`[JobDetail] Job ${id} loaded:`, data?.id);
      setJob(data);
      // Load evidence - wrapped in try/catch to not fail the whole page
      try {
        const evidenceData = await jobsService.listEvidence(id);
        setEvidence(evidenceData || []);
      } catch (evErr) {
        console.warn(`[JobDetail] Failed to load evidence for job ${id}:`, evErr);
        setEvidence([]);
      }
    } catch (err) {
      const status = err.response?.status || 'unknown';
      const serverMsg = err.response?.data?.detail || err.message || 'Unknown error';
      console.error(`[JobDetail] Failed to load job ${id}:`, { status, error: err.response?.data });
      setLoadError({ status, message: serverMsg });
      toast({
        title: 'Error',
        description: `Failed to load job (HTTP ${status}): ${serverMsg}`,
        variant: 'destructive'
      });
      // Navigate back to schedule instead of /jobs (which may not exist)
      navigate('/schedule');
    } finally {
      setLoading(false);
    }
  };

  const loadAuditors = async () => {
    try {
      const data = await auditorsService.list({ is_active: true });
      setAuditors(data || []);
    } catch (err) {
      console.error('[JobDetail] Failed to load auditors', err);
      setAuditors([]);
    }
  };

  // Participant handlers
  const handleAddParticipant = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await jobsService.addParticipant(id, {
        auditor_id: parseInt(participantForm.auditor_id),
        is_primary: participantForm.is_primary
      });
      toast({ title: 'Success', description: 'Auditor assigned' });
      setParticipantDialogOpen(false);
      setParticipantForm({ auditor_id: '', is_primary: false });
      loadJob();
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to assign auditor'),
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSetPrimary = async (participantId) => {
    try {
      await jobsService.setPrimaryAuditor(id, participantId);
      toast({ title: 'Success', description: 'Primary auditor set' });
      loadJob();
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to set primary'),
        variant: 'destructive'
      });
    }
  };

  const handleRemoveParticipant = async () => {
    try {
      await jobsService.removeParticipant(id, deleteParticipantId);
      toast({ title: 'Success', description: 'Auditor removed' });
      setDeleteParticipantId(null);
      loadJob();
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to remove auditor'),
        variant: 'destructive'
      });
    }
  };

  // Evidence handlers
  const handleAddEvidence = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await jobsService.addEvidence(id, evidenceForm);
      toast({ title: 'Success', description: 'Evidence added' });
      setEvidenceDialogOpen(false);
      setEvidenceForm({ evidence_type: 'note', description: '' });
      try {
        const evidenceData = await jobsService.listEvidence(id);
        setEvidence(evidenceData || []);
      } catch (evErr) {
        console.warn('Failed to reload evidence:', evErr);
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to add evidence'),
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Safe access to assignments with null checks
  const assignedAuditorIds = job?.assignments?.map(a => a?.auditor_id).filter(Boolean) || [];
  const availableAuditors = (auditors || []).filter(a => !assignedAuditorIds.includes(a.id));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Show error state with details if load failed
  if (loadError || !job) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <p className="text-lg font-medium">Job not found</p>
        {loadError && (
          <p className="text-sm text-muted-foreground">
            Error {loadError.status}: {loadError.message}
          </p>
        )}
        <Button onClick={() => navigate('/schedule')} variant="outline">
          Back to Schedule
        </Button>
      </div>
    );
  }

  const canModify = canManageJobs && job.status !== 'completed' && job.status !== 'cancelled';

  // Safe job status - default to scheduled if missing
  const jobStatus = job.status || 'scheduled';

  return (
    <ErrorBoundary fallbackTitle="Error loading job details" onGoBack={() => navigate('/schedule')}>
    <div data-testid="job-detail-page">
      <Toaster />

      {/* Header - Removed "Change Status" button as status changes should be done via Schedule page */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold" data-testid="job-title">{job.site?.name || `Job #${job.id}`}</h1>
            <Badge variant={statusColors[jobStatus] || 'default'} data-testid="job-status">
              {statusLabels[jobStatus] || jobStatus}
            </Badge>
          </div>
          {job.client_reference && (
            <p className="text-muted-foreground">Ref: {job.client_reference}</p>
          )}
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid gap-4 md:grid-cols-3 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <MapPin className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Site</p>
                <p className="font-medium">{job.site?.name || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Calendar className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Date</p>
                <p className="font-medium">
                  {job.scheduled_date ? format(new Date(job.scheduled_date), 'd MMM yyyy') : 'Not set'}
                  {job.end_date && job.end_date !== job.scheduled_date && (
                    <span> - {format(new Date(job.end_date), 'd MMM yyyy')}</span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Users className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Auditors</p>
                {/* Show auditor names or count */}
                {job.auditors && job.auditors.length > 0 ? (
                  <p className="font-medium">
                    {job.auditors.slice(0, 2).map(a => `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email).join(', ')}
                    {job.auditors.length > 2 && ` +${job.auditors.length - 2} more`}
                  </p>
                ) : job.assignments && job.assignments.length > 0 ? (
                  <p className="font-medium">
                    {job.assignments.slice(0, 2).map(a => `${a.auditor?.first_name || ''} ${a.auditor?.last_name || ''}`.trim() || 'Unknown').join(', ')}
                    {job.assignments.length > 2 && ` +${job.assignments.length - 2} more`}
                  </p>
                ) : (
                  <p className="font-medium text-muted-foreground">No auditors assigned</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs - Components tab hidden as requested */}
      <Tabs defaultValue="reports" className="space-y-4">
        <TabsList>
          <TabsTrigger value="participants" data-testid="tab-participants">
            <Users className="h-4 w-4 mr-2" />
            Auditors ({job.assignments?.length || job.auditors?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">
            <FolderOpen className="h-4 w-4 mr-2" />
            Reports
          </TabsTrigger>
          <TabsTrigger value="evidence" data-testid="tab-evidence">
            <FileText className="h-4 w-4 mr-2" />
            Evidence ({evidence?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* Participants Tab */}
        <TabsContent value="participants">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Assigned Auditors</CardTitle>
                <CardDescription>Team members working on this job</CardDescription>
              </div>
              {canModify && availableAuditors.length > 0 && (
                <Button onClick={() => setParticipantDialogOpen(true)} data-testid="add-participant-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Assign Auditor
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {/* Check both assignments and auditors arrays */}
              {(!job.assignments || job.assignments.length === 0) && (!job.auditors || job.auditors.length === 0) ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No auditors assigned yet</p>
                </div>
              ) : job.auditors && job.auditors.length > 0 ? (
                /* Use auditors array directly - it has full auditor details */
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Auditor</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Period</TableHead>
                      {canModify && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {job.auditors.map((auditor, index) => {
                      // iter454 — collect ALL assignments for this auditor
                      // (multi-range support). First assignment is used for
                      // action-button identity (setPrimary / delete target)
                      // — the actions themselves operate on a single row
                      // which is the existing behaviour.
                      const auditorAssignments = (job.assignments || [])
                        .filter(a => a.auditor_id === auditor.id)
                        .sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
                      const assignment = auditorAssignments[0]; // representative row for actions
                      const dateRangeLabel = auditorAssignments.length > 0
                        ? auditorAssignments.map(a => (
                            a.start_date === a.end_date
                              ? format(new Date(a.start_date), 'd MMM')
                              : `${format(new Date(a.start_date), 'd MMM')} - ${format(new Date(a.end_date), 'd MMM')}`
                          )).join(', ')
                        : '-';
                      return (
                        <TableRow key={auditor.id || auditor.user_id} data-testid={`auditor-row-${auditor.id || auditor.user_id}`}>
                          <TableCell className="font-medium">
                            {auditor.first_name || ''} {auditor.last_name || ''}
                          </TableCell>
                          <TableCell>{auditor.email || '-'}</TableCell>
                          <TableCell>
                            {index === 0 ? (
                              <Badge className="gap-1">
                                <Star className="h-3 w-3" /> Primary
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">Participant</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {dateRangeLabel}
                          </TableCell>
                          {canModify && (
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                {index !== 0 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleSetPrimary(assignment?.id || auditor.id)}
                                    data-testid={`set-primary-${auditor.id}-btn`}
                                  >
                                    <Star className="h-4 w-4 mr-1" />
                                    Set Primary
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setDeleteParticipantId(assignment?.id || auditor.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : job.assignments && job.assignments.length > 0 ? (
                /* Fallback to assignments if auditors array is empty */
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Auditor</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Assigned</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {job.assignments.map((p) => (
                      <TableRow key={p.id || p.auditor_id} data-testid={`participant-row-${p.id || p.auditor_id}`}>
                        <TableCell className="font-medium">
                          {p.auditor?.first_name || ''} {p.auditor?.last_name || ''}
                        </TableCell>
                        <TableCell>{p.auditor?.email || '-'}</TableCell>
                        <TableCell>
                          {p.is_primary ? (
                            <Badge className="gap-1">
                              <Star className="h-3 w-3" /> Primary
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">Participant</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {p.assigned_at ? format(new Date(p.assigned_at), 'd MMM yyyy') : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports">
          {/* Stock Reports Upload Section - Primary action */}
          <ClientReportsSection jobId={id} job={job} onUpdate={loadJob} />
          
          {/* Summary Stock Audit Report Section - Wizard */}
          <SummaryReportSection jobId={id} job={job} />
        </TabsContent>

        {/* Evidence Tab */}
        <TabsContent value="evidence">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Evidence & Notes</CardTitle>
                <CardDescription>Documentation and attachments</CardDescription>
              </div>
              <Button onClick={() => setEvidenceDialogOpen(true)} data-testid="add-evidence-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Evidence
              </Button>
            </CardHeader>
            <CardContent>
              {evidence.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No evidence recorded yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {evidence.map((e) => (
                    <div key={e.id} className="p-4 border rounded-lg" data-testid={`evidence-item-${e.id}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <Badge variant="secondary" className="mb-2">{e.evidence_type}</Badge>
                          <p className="text-sm">{e.description || 'No description'}</p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(e.uploaded_at), 'd MMM yyyy HH:mm')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Participant Dialog */}
      <Dialog open={participantDialogOpen} onOpenChange={setParticipantDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Auditor</DialogTitle>
            <DialogDescription>Add an auditor to this job</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddParticipant}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Auditor *</Label>
                <Select 
                  value={participantForm.auditor_id} 
                  onValueChange={(v) => setParticipantForm(f => ({ ...f, auditor_id: v }))}
                >
                  <SelectTrigger data-testid="participant-auditor-select">
                    <SelectValue placeholder="Select auditor" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableAuditors.map((a) => (
                      <SelectItem key={a.id} value={a.id.toString()}>
                        {a.first_name} {a.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_primary"
                  checked={participantForm.is_primary}
                  onChange={(e) => setParticipantForm(f => ({ ...f, is_primary: e.target.checked }))}
                  className="rounded"
                  data-testid="participant-primary-checkbox"
                />
                <Label htmlFor="is_primary">Set as Primary Auditor</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setParticipantDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={!participantForm.auditor_id || saving} data-testid="save-participant-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Assign Auditor
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Evidence Dialog */}
      <Dialog open={evidenceDialogOpen} onOpenChange={setEvidenceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Evidence</DialogTitle>
            <DialogDescription>Add notes or file references</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddEvidence}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={evidenceForm.evidence_type} 
                  onValueChange={(v) => setEvidenceForm(f => ({ ...f, evidence_type: v }))}
                >
                  <SelectTrigger data-testid="evidence-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="note">Note</SelectItem>
                    <SelectItem value="file">File Reference</SelectItem>
                    <SelectItem value="image">Image Reference</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={evidenceForm.description}
                  onChange={(e) => setEvidenceForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Enter your notes or file reference..."
                  required
                  data-testid="evidence-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEvidenceDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={!evidenceForm.description || saving} data-testid="save-evidence-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Add Evidence
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Participant Confirmation */}
      <AlertDialog open={!!deleteParticipantId} onOpenChange={() => setDeleteParticipantId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Auditor</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this auditor from the job?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveParticipant} className="bg-destructive text-destructive-foreground">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </ErrorBoundary>
  );
}
